import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, FileText, User, MapPin, CreditCard, Settings } from 'lucide-react';
import { ContratoWithDetails } from '@/types/contrato';

interface ContratoVisualizacaoProps {
  contratoAtual: ContratoWithDetails;
  loadingAction: boolean;
  onGerarPDF: () => void;
  onCriarNovo: () => void;
}

export default function ContratoVisualizacao({ 
  contratoAtual, 
  loadingAction, 
  onGerarPDF, 
  onCriarNovo 
}: ContratoVisualizacaoProps) {
  const getStatusBadge = (status: string) => {
    const statusConfig = {
      'rascunho': { label: 'Rascunho', variant: 'secondary' as const },
      'proposta_aceita': { label: 'Proposta Aceita', variant: 'default' as const },
      'contrato_gerado': { label: 'Contrato Gerado', variant: 'default' as const },
      'assinado': { label: 'Assinado', variant: 'default' as const }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.rascunho;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const formatCurrency = (value?: number) => {
    if (!value) return 'R$ 0,00';
    return `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
  };

  const formatCPF = (cpf?: string) => {
    if (!cpf) return 'Não informado';
    // Remove all non-digits
    const digits = cpf.replace(/\D/g, '');
    // Format as 000.000.000-00
    if (digits.length === 11) {
      return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    return cpf; // Return as-is if not 11 digits
  };

  const formatCNPJ = (cnpj?: string) => {
    if (!cnpj) return 'Não informado';
    // Remove all non-digits
    const digits = cnpj.replace(/\D/g, '');
    // Format as 00.000.000/0000-00
    if (digits.length === 14) {
      return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    return cnpj; // Return as-is if not 14 digits
  };

  const formatCEP = (cep?: string) => {
    if (!cep) return 'Não informado';
    // Remove all non-digits
    const digits = cep.replace(/\D/g, '');
    // Format as 00000-000
    if (digits.length === 8) {
      return digits.replace(/(\d{5})(\d{3})/, '$1-$2');
    }
    return cep; // Return as-is if not 8 digits
  };

  const formatPaymentMethod = (method?: string) => {
    const methods = {
      'boleto': 'Boleto',
      'pix': 'PIX',
      'cartao_credito': 'Cartão de Crédito',
      'cartao_debito': 'Cartão de Débito',
      'financiamento_bancario': 'Financiamento via instituição bancária'
    };
    return methods[method as keyof typeof methods] || method || 'Não informado';
  };

  const formatIntegrationModality = (modalidade?: number) => {
    const modalities = {
      1: 'Pagamento integral na assinatura',
      2: 'Assinatura + Conclusão',
      3: 'Assinatura + Início + Conclusão',
      4: 'Assinatura + Início + Conclusão + Ligação',
      5: 'Pagamento integral na conclusão da obra'
    };
    return modalities[modalidade as keyof typeof modalities] || 'Não informado';
  };

  const getClientName = () => {
    return contratoAtual.lead?.nome || contratoAtual.lead?.razao_social || contratoAtual.nome_cliente || contratoAtual.razao_social || 'Não informado';
  };

  const getClientType = () => {
    return contratoAtual.lead?.tipo || (contratoAtual.cpf ? 'PF' : 'PJ');
  };

  const getFullAddress = () => {
    const parts = [
      contratoAtual.endereco,
      contratoAtual.numero,
      contratoAtual.bairro,
      contratoAtual.cidade,
      contratoAtual.estado
    ].filter(Boolean);
    
    return parts.length > 0 ? parts.join(', ') : 'Não informado';
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Contrato Criado</CardTitle>
        <CardDescription>
          Contrato salvo com sucesso. Agora você pode gerar o PDF.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="bg-muted p-4 rounded-lg">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Resumo do Contrato
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Informações do Cliente */}
            <div className="space-y-3">
              <h4 className="font-medium text-sm text-muted-foreground flex items-center gap-2">
                <User className="w-4 h-4" />
                INFORMAÇÕES DO CLIENTE
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Nome/Razão Social:</span>
                  <span className="font-medium">{getClientName()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Tipo:</span>
                  <span className="font-medium">{getClientType()}</span>
                </div>
                {contratoAtual.cpf && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">CPF:</span>
                    <span className="font-medium">{formatCPF(contratoAtual.cpf)}</span>
                  </div>
                )}
                {contratoAtual.cnpj && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">CNPJ:</span>
                    <span className="font-medium">{formatCNPJ(contratoAtual.cnpj)}</span>
                  </div>
                )}
                {contratoAtual.representante_legal && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Representante Legal:</span>
                    <span className="font-medium">{contratoAtual.representante_legal}</span>
                  </div>
                )}
                {contratoAtual.cpf_representante && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">CPF do Representante:</span>
                    <span className="font-medium">{formatCPF(contratoAtual.cpf_representante)}</span>
                  </div>
                )}
                {contratoAtual.estado_civil && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Estado Civil:</span>
                    <span className="font-medium">{contratoAtual.estado_civil}</span>
                  </div>
                )}
                {contratoAtual.profissao && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Profissão:</span>
                    <span className="font-medium">{contratoAtual.profissao}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Endereço */}
            <div className="space-y-3">
              <h4 className="font-medium text-sm text-muted-foreground flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                ENDEREÇO
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Endereço:</span>
                  <span className="font-medium">{getFullAddress()}</span>
                </div>
                {contratoAtual.cep && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">CEP:</span>
                    <span className="font-medium">{formatCEP(contratoAtual.cep)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Valores Financeiros */}
            <div className="space-y-3">
              <h4 className="font-medium text-sm text-muted-foreground flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                VALORES FINANCEIROS
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Valor do Kit:</span>
                  <span className="font-medium">{formatCurrency(contratoAtual.valor_kit)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Valor da Integração:</span>
                  <span className="font-medium">{formatCurrency(contratoAtual.valor_integracao)}</span>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <span className="text-muted-foreground font-semibold">Valor Total:</span>
                  <span className="font-bold text-lg">{formatCurrency(contratoAtual.valor_total)}</span>
                </div>
              </div>
            </div>

            {/* Status */}
            <div className="space-y-3">
              <h4 className="font-medium text-sm text-muted-foreground flex items-center gap-2">
                <Settings className="w-4 h-4" />
                STATUS E INFORMAÇÕES
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Status:</span>
                  <span>{getStatusBadge(contratoAtual.status)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Criado em:</span>
                  <span className="font-medium">
                    {new Date(contratoAtual.created_at).toLocaleDateString('pt-BR')}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Atualizado em:</span>
                  <span className="font-medium">
                    {new Date(contratoAtual.updated_at).toLocaleDateString('pt-BR')}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Pagamentos */}
          <div className="mt-6 space-y-4">
            <h4 className="font-medium text-sm text-muted-foreground flex items-center gap-2">
              <CreditCard className="w-4 h-4" />
              FORMAS DE PAGAMENTO
            </h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Pagamento do Kit */}
              <div className="bg-background p-3 rounded-lg border">
                <h5 className="font-medium text-sm mb-2">💰 Pagamento do Kit</h5>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Forma:</span>
                    <span className="font-medium">{formatPaymentMethod(contratoAtual.kit_forma_pagamento)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Valor:</span>
                    <span className="font-medium">{formatCurrency(contratoAtual.kit_valor_pagamento)}</span>
                  </div>
                  {contratoAtual.kit_numero_parcelas && contratoAtual.kit_numero_parcelas > 1 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Parcelas:</span>
                      <span className="font-medium">{contratoAtual.kit_numero_parcelas}x de {formatCurrency(contratoAtual.kit_valor_parcela)}</span>
                    </div>
                  )}
                  {contratoAtual.kit_observacoes && (
                    <div className="mt-2">
                      <span className="text-muted-foreground text-xs">Observações:</span>
                      <p className="text-xs mt-1">{contratoAtual.kit_observacoes}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Pagamento da Integração */}
              <div className="bg-background p-3 rounded-lg border">
                <h5 className="font-medium text-sm mb-2">🔧 Pagamento da Integração</h5>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Modalidade:</span>
                    <span className="font-medium text-xs">{formatIntegrationModality(contratoAtual.integracao_modalidade_pagamento)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Valor Total:</span>
                    <span className="font-medium">{formatCurrency(contratoAtual.integracao_valor_total)}</span>
                  </div>
                  
                  {/* Formas de pagamento por etapa */}
                  {contratoAtual.integracao_modalidade_pagamento === 1 && contratoAtual.integracao_forma_pagamento_assinatura && (
                    <div className="mt-2 pt-2 border-t space-y-1">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Assinatura:</span>
                        <span className="font-medium">{formatPaymentMethod(contratoAtual.integracao_forma_pagamento_assinatura)}</span>
                      </div>
                      {contratoAtual.integracao_forma_pagamento_assinatura === 'cartao_credito' && contratoAtual.integracao_numero_parcelas_assinatura && contratoAtual.integracao_numero_parcelas_assinatura > 1 && (
                        <div className="flex justify-between text-xs ml-4">
                          <span className="text-muted-foreground">Parcelas:</span>
                          <span className="font-medium">{contratoAtual.integracao_numero_parcelas_assinatura}x de {formatCurrency(contratoAtual.integracao_valor_parcela_assinatura)}</span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {contratoAtual.integracao_modalidade_pagamento === 2 && (
                    <>
                      {contratoAtual.integracao_forma_pagamento_assinatura && (
                        <div className="mt-2 pt-2 border-t space-y-1">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Assinatura:</span>
                            <span className="font-medium">{formatPaymentMethod(contratoAtual.integracao_forma_pagamento_assinatura)}</span>
                          </div>
                          {contratoAtual.integracao_forma_pagamento_assinatura === 'cartao_credito' && contratoAtual.integracao_numero_parcelas_assinatura && contratoAtual.integracao_numero_parcelas_assinatura > 1 && (
                            <div className="flex justify-between text-xs ml-4">
                              <span className="text-muted-foreground">Parcelas:</span>
                              <span className="font-medium">{contratoAtual.integracao_numero_parcelas_assinatura}x de {formatCurrency(contratoAtual.integracao_valor_parcela_assinatura)}</span>
                            </div>
                          )}
                        </div>
                      )}
                      {contratoAtual.integracao_forma_pagamento_conclusao_obra && (
                        <div className="space-y-1">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Conclusão:</span>
                            <span className="font-medium">{formatPaymentMethod(contratoAtual.integracao_forma_pagamento_conclusao_obra)}</span>
                          </div>
                          {contratoAtual.integracao_forma_pagamento_conclusao_obra === 'cartao_credito' && contratoAtual.integracao_numero_parcelas_conclusao_obra && contratoAtual.integracao_numero_parcelas_conclusao_obra > 1 && (
                            <div className="flex justify-between text-xs ml-4">
                              <span className="text-muted-foreground">Parcelas:</span>
                              <span className="font-medium">{contratoAtual.integracao_numero_parcelas_conclusao_obra}x de {formatCurrency(contratoAtual.integracao_valor_parcela_conclusao_obra)}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                  
                  {contratoAtual.integracao_modalidade_pagamento === 3 && (
                    <>
                      {contratoAtual.integracao_forma_pagamento_assinatura && (
                        <div className="mt-2 pt-2 border-t space-y-1">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Assinatura:</span>
                            <span className="font-medium">{formatPaymentMethod(contratoAtual.integracao_forma_pagamento_assinatura)}</span>
                          </div>
                          {contratoAtual.integracao_forma_pagamento_assinatura === 'cartao_credito' && contratoAtual.integracao_numero_parcelas_assinatura && contratoAtual.integracao_numero_parcelas_assinatura > 1 && (
                            <div className="flex justify-between text-xs ml-4">
                              <span className="text-muted-foreground">Parcelas:</span>
                              <span className="font-medium">{contratoAtual.integracao_numero_parcelas_assinatura}x de {formatCurrency(contratoAtual.integracao_valor_parcela_assinatura)}</span>
                            </div>
                          )}
                        </div>
                      )}
                      {contratoAtual.integracao_forma_pagamento_inicio_obra && (
                        <div className="space-y-1">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Início:</span>
                            <span className="font-medium">{formatPaymentMethod(contratoAtual.integracao_forma_pagamento_inicio_obra)}</span>
                          </div>
                          {contratoAtual.integracao_forma_pagamento_inicio_obra === 'cartao_credito' && contratoAtual.integracao_numero_parcelas_inicio_obra && contratoAtual.integracao_numero_parcelas_inicio_obra > 1 && (
                            <div className="flex justify-between text-xs ml-4">
                              <span className="text-muted-foreground">Parcelas:</span>
                              <span className="font-medium">{contratoAtual.integracao_numero_parcelas_inicio_obra}x de {formatCurrency(contratoAtual.integracao_valor_parcela_inicio_obra)}</span>
                            </div>
                          )}
                        </div>
                      )}
                      {contratoAtual.integracao_forma_pagamento_conclusao_obra && (
                        <div className="space-y-1">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Conclusão:</span>
                            <span className="font-medium">{formatPaymentMethod(contratoAtual.integracao_forma_pagamento_conclusao_obra)}</span>
                          </div>
                          {contratoAtual.integracao_forma_pagamento_conclusao_obra === 'cartao_credito' && contratoAtual.integracao_numero_parcelas_conclusao_obra && contratoAtual.integracao_numero_parcelas_conclusao_obra > 1 && (
                            <div className="flex justify-between text-xs ml-4">
                              <span className="text-muted-foreground">Parcelas:</span>
                              <span className="font-medium">{contratoAtual.integracao_numero_parcelas_conclusao_obra}x de {formatCurrency(contratoAtual.integracao_valor_parcela_conclusao_obra)}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                  
                  {contratoAtual.integracao_modalidade_pagamento === 4 && (
                    <>
                      {contratoAtual.integracao_forma_pagamento_assinatura && (
                        <div className="mt-2 pt-2 border-t space-y-1">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Assinatura:</span>
                            <span className="font-medium">{formatPaymentMethod(contratoAtual.integracao_forma_pagamento_assinatura)}</span>
                          </div>
                          {contratoAtual.integracao_forma_pagamento_assinatura === 'cartao_credito' && contratoAtual.integracao_numero_parcelas_assinatura && contratoAtual.integracao_numero_parcelas_assinatura > 1 && (
                            <div className="flex justify-between text-xs ml-4">
                              <span className="text-muted-foreground">Parcelas:</span>
                              <span className="font-medium">{contratoAtual.integracao_numero_parcelas_assinatura}x de {formatCurrency(contratoAtual.integracao_valor_parcela_assinatura)}</span>
                            </div>
                          )}
                        </div>
                      )}
                      {contratoAtual.integracao_forma_pagamento_inicio_obra && (
                        <div className="space-y-1">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Início:</span>
                            <span className="font-medium">{formatPaymentMethod(contratoAtual.integracao_forma_pagamento_inicio_obra)}</span>
                          </div>
                          {contratoAtual.integracao_forma_pagamento_inicio_obra === 'cartao_credito' && contratoAtual.integracao_numero_parcelas_inicio_obra && contratoAtual.integracao_numero_parcelas_inicio_obra > 1 && (
                            <div className="flex justify-between text-xs ml-4">
                              <span className="text-muted-foreground">Parcelas:</span>
                              <span className="font-medium">{contratoAtual.integracao_numero_parcelas_inicio_obra}x de {formatCurrency(contratoAtual.integracao_valor_parcela_inicio_obra)}</span>
                            </div>
                          )}
                        </div>
                      )}
                      {contratoAtual.integracao_forma_pagamento_conclusao_obra && (
                        <div className="space-y-1">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Conclusão:</span>
                            <span className="font-medium">{formatPaymentMethod(contratoAtual.integracao_forma_pagamento_conclusao_obra)}</span>
                          </div>
                          {contratoAtual.integracao_forma_pagamento_conclusao_obra === 'cartao_credito' && contratoAtual.integracao_numero_parcelas_conclusao_obra && contratoAtual.integracao_numero_parcelas_conclusao_obra > 1 && (
                            <div className="flex justify-between text-xs ml-4">
                              <span className="text-muted-foreground">Parcelas:</span>
                              <span className="font-medium">{contratoAtual.integracao_numero_parcelas_conclusao_obra}x de {formatCurrency(contratoAtual.integracao_valor_parcela_conclusao_obra)}</span>
                            </div>
                          )}
                        </div>
                      )}
                      {contratoAtual.integracao_forma_pagamento_ligacao_definitiva && (
                        <div className="space-y-1">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Ligação:</span>
                            <span className="font-medium">{formatPaymentMethod(contratoAtual.integracao_forma_pagamento_ligacao_definitiva)}</span>
                          </div>
                          {contratoAtual.integracao_forma_pagamento_ligacao_definitiva === 'cartao_credito' && contratoAtual.integracao_numero_parcelas_ligacao_definitiva && contratoAtual.integracao_numero_parcelas_ligacao_definitiva > 1 && (
                            <div className="flex justify-between text-xs ml-4">
                              <span className="text-muted-foreground">Parcelas:</span>
                              <span className="font-medium">{contratoAtual.integracao_numero_parcelas_ligacao_definitiva}x de {formatCurrency(contratoAtual.integracao_valor_parcela_ligacao_definitiva)}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                  
                  {contratoAtual.integracao_modalidade_pagamento === 5 && contratoAtual.integracao_forma_pagamento_conclusao_obra && (
                    <div className="mt-2 pt-2 border-t space-y-1">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Conclusão:</span>
                        <span className="font-medium">{formatPaymentMethod(contratoAtual.integracao_forma_pagamento_conclusao_obra)}</span>
                      </div>
                      {contratoAtual.integracao_forma_pagamento_conclusao_obra === 'cartao_credito' && contratoAtual.integracao_numero_parcelas_conclusao_obra && contratoAtual.integracao_numero_parcelas_conclusao_obra > 1 && (
                        <div className="flex justify-between text-xs ml-4">
                          <span className="text-muted-foreground">Parcelas:</span>
                          <span className="font-medium">{contratoAtual.integracao_numero_parcelas_conclusao_obra}x de {formatCurrency(contratoAtual.integracao_valor_parcela_conclusao_obra)}</span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {contratoAtual.integracao_observacoes && (
                    <div className="mt-2 pt-2 border-t">
                      <span className="text-muted-foreground text-xs">Observações:</span>
                      <p className="text-xs mt-1">{contratoAtual.integracao_observacoes}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Detalhamento dos Percentuais da Integração */}
            {contratoAtual.integracao_modalidade_pagamento != null && contratoAtual.integracao_modalidade_pagamento > 1 && contratoAtual.integracao_modalidade_pagamento !== 5 && (
              <div className="bg-background p-3 rounded-lg border">
                <h5 className="font-medium text-sm mb-2">📊 Detalhamento dos Percentuais da Integração</h5>
                <div className="flex flex-wrap gap-4 text-sm">
                  {(contratoAtual.integracao_percentual_assinatura ?? 0) > 0 && (
                    <div>
                      <span className="text-muted-foreground">Assinatura:</span>{' '}
                      <span className="font-medium">{contratoAtual.integracao_percentual_assinatura}% ({formatCurrency(contratoAtual.integracao_valor_assinatura)})</span>
                    </div>
                  )}
                  {(contratoAtual.integracao_percentual_inicio_obra ?? 0) > 0 && (
                    <div>
                      <span className="text-muted-foreground">Início Obra:</span>{' '}
                      <span className="font-medium">{contratoAtual.integracao_percentual_inicio_obra}% ({formatCurrency(contratoAtual.integracao_valor_inicio_obra)})</span>
                    </div>
                  )}
                  {(contratoAtual.integracao_percentual_conclusao_obra ?? 0) > 0 && (
                    <div>
                      <span className="text-muted-foreground">Conclusão:</span>{' '}
                      <span className="font-medium">{contratoAtual.integracao_percentual_conclusao_obra}% ({formatCurrency(contratoAtual.integracao_valor_conclusao_obra)})</span>
                    </div>
                  )}
                  {(contratoAtual.integracao_percentual_ligacao_definitiva ?? 0) > 0 && (
                    <div>
                      <span className="text-muted-foreground">Ligação:</span>{' '}
                      <span className="font-medium">{contratoAtual.integracao_percentual_ligacao_definitiva}% ({formatCurrency(contratoAtual.integracao_valor_ligacao_definitiva)})</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Detalhamento dos Percentuais da Integração - Modalidade 5 */}
            {contratoAtual.integracao_modalidade_pagamento === 5 && (
              <div className="bg-background p-3 rounded-lg border">
                <h5 className="font-medium text-sm mb-2">📊 Detalhamento dos Percentuais da Integração</h5>
                <div className="flex flex-wrap gap-4 text-sm">
                  {(contratoAtual.integracao_percentual_conclusao_obra ?? 0) > 0 && (
                    <div>
                      <span className="text-muted-foreground">Conclusão:</span>{' '}
                      <span className="font-medium">{contratoAtual.integracao_percentual_conclusao_obra}% ({formatCurrency(contratoAtual.integracao_valor_conclusao_obra)})</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-2">
          <Button onClick={onGerarPDF} disabled={loadingAction}>
            {loadingAction && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            <FileText className="w-4 h-4 mr-2" />
            Gerar Contrato PDF
          </Button>
          <Button variant="outline" onClick={onCriarNovo}>
            Criar Novo Contrato
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
